import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Play, Heart, Eye, Clock, Users, Share2, 
  Download, Music, Grid, List, Filter, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';


const API = process.env.REACT_APP_BACKEND_URL;

function StudioGalleryInner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('recent');
  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingItem, setLoadingItem] = useState(false);

  const perPage = 12;

  useEffect(() => {
    fetchGallery();
  }, [page, sort]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/studio-gallery?page=${page}&per_page=${perPage}&sort=${sort}`);
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const loadItem = async (galleryId) => {
    try {
      setLoadingItem(true);
      const res = await fetch(`${API}/api/studio-gallery/${galleryId}`);
      const data = await res.json();
      setSelectedItem(data);
    } catch (err) {
      console.error('Failed to load item:', err);
      toast.error('Failed to load recording');
    } finally {
      setLoadingItem(false);
    }
  };

  const handleLike = async (galleryId) => {
    if (!user) {
      toast.error('Please log in to like recordings');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('user_id', user.id);
      
      const res = await fetch(`${API}/api/studio-gallery/${galleryId}/like`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.gallery_id === galleryId 
          ? { ...item, likes: data.likes }
          : item
      ));
      
      if (selectedItem?.gallery_id === galleryId) {
        setSelectedItem(prev => ({ ...prev, likes: data.likes }));
      }
      
    } catch (err) {
      console.error('Failed to like:', err);
    }
  };

  const handleShare = async (item) => {
    const shareUrl = `${window.location.origin}/studio-gallery?play=${item.gallery_id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.title,
          text: `Check out "${item.title}" on Escola de Samba Casa Samba Mobile Studio!`,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const handleDownload = (item) => {
    if (!item.video_data) {
      toast.error('Video not loaded yet');
      return;
    }
    
    const link = document.createElement('a');
    link.href = item.video_data;
    link.download = `${item.title.replace(/\s+/g, '_')}.webm`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started!');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 to-stone-900 text-white">
      {/* Header */}
      <header className="sticky top-0 bg-stone-950/95 backdrop-blur-sm border-b border-stone-800 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/mobile-studio" className="p-2 hover:bg-stone-800 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <PageEditable
                path="hero.title"
                as="h1"
                fallback="Community Recordings"
                className="text-xl font-bold"
              />
              <PageEditable
                path="hero.subtitle"
                as="p"
                fallback="Discover recordings from the community"
                className="text-sm text-stone-400"
              />
            </div>
          </div>
          <Link 
            to="/mobile-studio"
            className="px-4 py-2 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400"
          >
            Create
          </Link>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 bg-stone-800 rounded-lg p-1">
            {['recent', 'popular', 'liked'].map((s) => (
              <button
                key={s}
                onClick={() => setSort(s)}
                className={`px-3 py-1.5 rounded text-sm font-medium capitalize ${
                  sort === s ? 'bg-amber-500 text-black' : 'text-stone-400 hover:text-white'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <span className="text-stone-500 text-sm">{total} recordings</span>
        </div>
      </div>

      {/* Gallery Grid */}
      <main className="max-w-6xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-stone-800 rounded-xl aspect-video animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Music className="w-16 h-16 mx-auto text-stone-600 mb-4" />
            <h2 className="text-xl font-bold mb-2">No recordings yet</h2>
            <p className="text-stone-400 mb-6">Be the first to share your creation!</p>
            <Link 
              to="/mobile-studio"
              className="px-6 py-3 bg-amber-500 text-black font-bold rounded-lg inline-block"
            >
              Start Recording
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => (
                <div 
                  key={item.gallery_id}
                  className="bg-stone-800 rounded-xl overflow-hidden hover:ring-2 hover:ring-amber-500 transition-all cursor-pointer group"
                  onClick={() => loadItem(item.gallery_id)}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-stone-900 relative">
                    {item.thumbnail ? (
                      <img loading="lazy" decoding="async" 
                        src={item.thumbnail} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-12 h-12 text-stone-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Play className="w-12 h-12 text-white" />
                    </div>
                    {item.duration > 0 && (
                      <span className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5 rounded text-xs">
                        {formatDuration(item.duration)}
                      </span>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-bold truncate">{item.title}</h3>
                    <p className="text-sm text-stone-400 truncate">by {item.username}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {item.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" /> {item.likes}
                      </span>
                      <span className="flex items-center gap-1">
                        <Music className="w-3 h-3" /> {item.track_count} tracks
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-lg font-bold ${
                      page === i + 1 ? 'bg-amber-500 text-black' : 'bg-stone-800 hover:bg-stone-700'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Video Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="bg-stone-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {loadingItem ? (
              <div className="aspect-video flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Video */}
                <div className="aspect-video bg-black">
                  {selectedItem.video_data ? (
                    <video 
                      src={selectedItem.video_data} 
                      controls 
                      autoPlay
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-500">
                      Loading video...
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-bold">{selectedItem.title}</h2>
                      <p className="text-stone-400">by {selectedItem.username}</p>
                      {selectedItem.description && (
                        <p className="mt-2 text-sm text-stone-300">{selectedItem.description}</p>
                      )}
                    </div>
                    <button 
                      onClick={() => setSelectedItem(null)}
                      className="p-2 hover:bg-stone-800 rounded-lg"
                    >
                      ✕
                    </button>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-6 mt-4 text-sm text-stone-400">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> {selectedItem.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="w-4 h-4" /> {selectedItem.likes} likes
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {formatDate(selectedItem.created_at)}
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-3 mt-4">
                    <button 
                      onClick={() => handleLike(selectedItem.gallery_id)}
                      className="flex items-center gap-2 px-4 py-2 bg-pink-600 rounded-lg font-bold hover:bg-pink-500"
                    >
                      <Heart className="w-4 h-4" /> Like
                    </button>
                    <button 
                      onClick={() => handleShare(selectedItem)}
                      className="flex items-center gap-2 px-4 py-2 bg-stone-700 rounded-lg font-bold hover:bg-stone-600"
                    >
                      <Share2 className="w-4 h-4" /> Share
                    </button>
                    <button 
                      onClick={() => handleDownload(selectedItem)}
                      className="flex items-center gap-2 px-4 py-2 bg-stone-700 rounded-lg font-bold hover:bg-stone-600"
                    >
                      <Download className="w-4 h-4" /> Download
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const StudioGallery = () => (
  <>
    <StudioGalleryInner />
    <AdminEditorLink to="/admin/gallery" label="Edit Studio Gallery" />
  </>
);

export { StudioGallery };
export default StudioGallery;
